import React, { HTMLAttributes, createContext, useContext, useEffect, useState } from "react";
import { TextareaWithMaxLength } from "@/components/fun-ui/TextareaWithMaxLength";
import { Button } from "@/components/fun-ui/Button";
import { Label } from "@/components/ui/label";
import { useAiCol, useAiColsConfig } from "@/stores/appStore";
import { CreateAiColOptions, PreCreateAiColResult } from "@/stores/slices/paperSlice";
import { cn } from "@/lib/utils";
import { useSubjectId } from "@/components/subjects/hooks/usePathParams";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDownIcon } from "lucide-react";
import { AiColLang } from "@/stores/slices/lib/paperTypes";
import { DEFAULT_LANG, LANG_LIST } from "./lib/aiColsConst";
import { MarkdownRenderer } from "@/components/papers/PaperDetail/MarkdownRenderer";
import { LoaderIcon } from "@/components/fun-ui/icons/LoaderIcon";
import Loading from "@/components/Loading";

type CreateCustomAIColContextType = {
  showForm: boolean;
  setShowForm: (v: boolean) => void;
};

const CreateCustomAIColContext = createContext<CreateCustomAIColContextType | undefined>(undefined);

export const useCreateCustomAICol = () => {
  const ctx = useContext(CreateCustomAIColContext);
  if (!ctx) throw new Error("useCreateCustomAICol must be used within CustomAIColFormWithProvider");
  return ctx;
};

export const CreateCustomAIColButton = ({
  className = '',
  disabled = false,
}: {
  className?: string;
  disabled?: boolean;
}) => {
  const { setShowForm } = useCreateCustomAICol();
  return (
    <Button
      variant="outline"
      disabled={disabled}
      className={
        cn(
          "border-primary text-primary min-w-[126px] hover:bg-primary/10 hover:text-primary",
          className,
        )
      }
      onClick={() => {
        if (!disabled) {
          setShowForm(true);
        }
      }}
    >创建 AI 表格列</Button>
  );
};

export const CustomAIColForm: React.FC<{
  children: React.ReactNode;
  onShowFormChange?: (show: boolean) => void;
}> = ({ children, onShowFormChange }) => {
  const [showForm, setShowForm] = useState(false);

  // 表单相关 state
  const [colData, setColData] = useState<CreateAiColOptions>({
    colName: '',
    colDesc: '',
    lang: DEFAULT_LANG,
  });
  const [colHidden, setColHidden] = useState({
    colName: true,
    colDesc: true,
  });
  const [previewData, setPreviewData] = useState<PreCreateAiColResult | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [preCreateLoading, setPreCreateLoading] = useState(false);
  const subjectId = useSubjectId();

  const { createAiCol, preCreateAiCol } = useAiCol();
  const [submitLoading, setSubmitLoading] = useState(false);

  // context 只暴露显隐控制
  const contextValue = { showForm, setShowForm };

  const {
    fetch: fetchAiColsConfig,
    value: aiColsConfig,
  } = useAiColsConfig();

  useEffect(() => {
    fetchAiColsConfig();
  }, [fetchAiColsConfig]);

  useEffect(() => {
    onShowFormChange?.(showForm);
  }, [onShowFormChange, showForm]);

  const resetForm = () => {
    setColData({
      colName: '',
      colDesc: '',
      lang: DEFAULT_LANG,
    });
    setColHidden({
      colName: true,
      colDesc: true,
    });
    setPreviewData(null);
    setShowConfirm(false);
    setPreCreateLoading(false);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  return (
    <CreateCustomAIColContext.Provider value={contextValue}>
      {!showForm ? (
        children
      ) : (
        <div className="w-full">
          <div className="text-sm font-semibold">创建 AI 表格列</div>
          <div className="text-sm text-muted-foreground mt-1">{`AI 将根据你的“要求”从文献中提取信息，每生成 1 个单元格消耗 ${aiColsConfig?.customPrice} 个飞豆`}</div>
          <div className="relative min-h-[420px]">
            {!showConfirm ? (
            <>
              <div>
                <Label className="flex gap-1 font-normal text-muted-foreground mt-6">
                  <span>表格列名称</span>
                  <span className="text-destructive">*</span>
                </Label>
                <TextareaWithMaxLength
                  id="customAIColName"
                  rows={1}
                  containerClassName="bg-transparent mt-3"
                  className="border border-solid border-border placeholder:text-annotation-foreground"
                  maxLength={10}
                  value={colData?.colName ?? ''}
                  placeholder="如：核心观点（ 10 字以内）"
                  onChange={(e) => {
                    const colName = e.currentTarget.value;
                    setColData({
                      ...colData,
                      colName,
                    });
                    setColHidden({
                      ...colHidden,
                      colName: !!colName,
                    });
                  }}
                  autoComplete="off"
                  disabled={submitLoading}
                />
                <div hidden={colHidden.colName} className="mt-2 text-xs text-destructive">请输入表格列名称</div>
              </div>
              <div>
                <Label className="flex gap-1 font-normal text-muted-foreground mt-6">
                  <span>表格列输出语言</span>
                  <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={colData.lang}
                  onValueChange={(value) => {
                    setColData({
                      ...colData,
                      lang: (value as AiColLang),
                    });
                  }}
                >
                  <SelectTrigger
                    className={cn(
                      "border-border outline-none p-3 shadow-none mt-3",
                      "w-full whitespace-nowrap h-10",
                    )}
                  >
                    <SelectValue />
                    <ChevronDownIcon className="text-annotation-foreground" size={16} />
                  </SelectTrigger>
                  <SelectContent>
                    {
                      LANG_LIST.map(({ value, label }) => {
                        return (
                          <SelectItem
                            key={value}
                            value={value}
                          >
                            {label}
                          </SelectItem>
                        );
                      })
                    }
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex gap-1 font-normal text-muted-foreground mt-6">
                  <span>表格列提取要求</span>
                  <span className="text-destructive">*</span>
                </Label>
                <TextareaWithMaxLength
                  containerClassName="bg-transparent mt-3"
                  className="border border-solid border-border placeholder:text-annotation-foreground p-3 min-h-[144px]"
                  placeholder="如：列举总结文献的核心观点"
                  id="customAIColDesc"
                  value={colData?.colDesc ?? ''}
                  onChange={(e) => {
                    const colDesc = e.currentTarget.value;
                    setColData({
                      ...colData,
                      colDesc,
                    });
                    setColHidden({
                      ...colHidden,
                      colDesc: !!colDesc,
                    });
                  }}
                  maxLength={150}
                  disabled={submitLoading}
                />
                <div hidden={colHidden.colDesc} className="mt-2 text-xs text-destructive">请输入表格列提取要求</div>
              </div>
              <div className="flex flex-row gap-3 mt-3">
                <Button
                  className="flex-1"
                  variant="outline"
                  disabled={submitLoading}
                  onClick={closeForm}
                >取消</Button>
                <Button
                  className="flex-1"
                  variant="default"
                  disabled={submitLoading || (!colData.colDesc || !colData.colName)}
                  onClick={async () => {
                    const { colName, colDesc, lang } = colData;
                    if (!colName || !colDesc) {
                      setColHidden({
                        colName: !!colName,
                        colDesc: !!colDesc,
                      });
                      return;
                    }

                    setSubmitLoading(true);
                    setPreCreateLoading(true);
                    try {
                      const result = await preCreateAiCol({
                        ...(previewData ?? {}),
                        colName,
                        colDesc,
                        lang,
                      });
                      if (result) {
                        const nextColName = typeof result.colName === 'string' ? result.colName : colName;
                        const nextColDesc = typeof result.colDesc === 'string' ? result.colDesc : colDesc;
                        const nextLang = typeof result.lang === 'string' ? (result.lang as AiColLang) : lang;
                        setColData({
                          colName: nextColName,
                          colDesc: nextColDesc,
                          lang: nextLang,
                        });
                        setPreviewData(result);
                        setShowConfirm(true);
                      }
                    } finally {
                      setPreCreateLoading(false);
                      setSubmitLoading(false);
                    }
                  }}
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    {submitLoading ? <LoaderIcon className="w-4 h-4" /> : null}
                    <span>确认</span>
                  </span>
                </Button>
              </div>
            </>
            ) : (
            <>
              <div className="text-sm text-muted-foreground mt-5">请确认表格列的提取要求</div>
              <div className="mt-3 rounded-md bg-muted p-3">
                <div className="text-sm leading-6 text-foreground break-all">
                  <MarkdownRenderer components={{
                    p: ({ className, children }: HTMLAttributes<HTMLParagraphElement>) => (
                      <p
                        className={cn("leading-6 text-sm [&:not(:first-child)]:mt-5", className)}
                      >{children}</p>
                    )
                  }}>
                    {typeof previewData?.requirementShow === 'string' ? previewData.requirementShow : ''}
                  </MarkdownRenderer>
                </div>
              </div>
              <div className="flex flex-row gap-3 mt-3">
                <Button
                  className="flex-1"
                  variant="outline"
                  disabled={submitLoading}
                  onClick={() => {
                    setShowConfirm(false);
                  }}
                >返回修改</Button>
                <Button
                  className="flex-1"
                  variant="default"
                  disabled={submitLoading || !previewData}
                  onClick={async () => {
                    if (!previewData) {
                      return;
                    }
                    setSubmitLoading(true);
                    try {
                      await createAiCol(previewData, subjectId);
                      closeForm();
                    } finally {
                      setSubmitLoading(false);
                    }
                  }}
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    {submitLoading ? <LoaderIcon className="w-4 h-4" /> : null}
                    <span>确认创建</span>
                  </span>
                </Button>
              </div>
            </>
            )}
            {preCreateLoading ? (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 bg-background">
                <Loading size="sm">
                  <div className="text-sm text-muted-foreground">提取要求识别中</div>
                </Loading>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </CreateCustomAIColContext.Provider>
  );
};
